import styled from "styled-components";

import { BeneficiaryType, Flow, Routes } from "@ctypes";
import { useErrorBanner, useGoalFlow, useGoalsContext, useNavigateToStep, useUser } from "@hooks";
import { Translations, i18n } from "@i18n";
import { Card, Eyebrow, SerifTitle } from "@routes/DashboardRoute/Home/styles";
import { home } from "@routes/DashboardRoute/Home/tokens";
import { GoalStanding, Standing, formatCurrency } from "@utils";

const Section = styled.div`
  padding: 44px ${home.pagePad} 0;
  display: flex;
  flex-direction: column;
  gap: 26px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const GoalCard = styled(Card).attrs({ as: "button", type: "button" })<{
  $standing: Standing;
}>`
  padding: 26px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  font-family: ${home.sans};
  color: ${home.ink};
  text-align: left;
  cursor: pointer;
  border-color: ${({ $standing }) =>
    $standing === Standing.Met
      ? "rgba(143,191,176,0.34)"
      : $standing === Standing.Short
        ? "rgba(224,155,124,0.30)"
        : home.line10};
  ${({ $standing }) =>
    $standing === Standing.Met &&
    `background: linear-gradient(155deg, rgba(143,191,176,0.18) 0%, rgba(20,26,40,0.72) 66%);`}
`;

const GoalName = styled.div`
  font-family: ${home.serif};
  font-size: 20px;
  letter-spacing: -0.2px;
`;

const Badge = styled.div<{ $standing: Standing }>`
  display: flex;
  align-items: center;
  gap: 7px;
  height: 26px;
  padding: 0 11px;
  border-radius: 999px;
  margin-left: auto;
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $standing }) =>
    $standing === Standing.Met
      ? "rgba(143,191,176,0.26)"
      : $standing === Standing.OnTrack
        ? "rgba(143,191,176,0.16)"
        : "rgba(224,155,124,0.20)"};
  color: ${({ $standing }) =>
    $standing === Standing.Met
      ? "#B7DCCE"
      : $standing === Standing.OnTrack
        ? "#9DCBBB"
        : "#E7B79E"};

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: currentColor;
    display: block;
  }
`;

const Amount = styled.div`
  font-size: 26px;
  font-weight: 600;
  letter-spacing: -0.7px;
  line-height: 1;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${home.ink52};
  margin-top: 8px;

  svg {
    margin-left: auto;
    flex-shrink: 0;
  }
`;

const AddStrip = styled.button`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 22px 26px;
  border-radius: 18px;
  border: 1px dashed ${home.line20};
  background: none;
  font-family: ${home.sans};
  color: ${home.ink};
  text-align: left;
  cursor: pointer;
  transition: background 160ms ease;

  &:hover {
    background: ${home.fill09};
  }

  .icon {
    width: 40px;
    height: 40px;
    border-radius: 999px;
    border: 1px solid ${home.line20};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .title {
    font-size: 15px;
    font-weight: 500;
  }

  .body {
    font-size: 13px;
    color: ${home.ink46};
    margin-top: 4px;
  }

  .cta {
    font-size: 13px;
    color: ${home.copper};
    margin-left: auto;
    flex-shrink: 0;
  }
`;

export const GoalStatusSection = ({
  standings,
  name,
}: {
  standings: Array<GoalStanding>;
  name: string;
}) => {
  const navigate = useNavigateToStep();
  const { showErrorBanner } = useErrorBanner();
  const { hasBankAccount } = useUser();
  const { resetGoalProps } = useGoalFlow();
  const { beneficiaryId, beneficiaryType = BeneficiaryType.Child } = useGoalsContext();

  const handleAddGoal = () => {
    if (!beneficiaryId) return showErrorBanner();

    if (hasBankAccount) {
      resetGoalProps({ flow: Flow.AddGoal, beneficiaryType, beneficiaryId });
      navigate(Routes.GoalType);
    } else {
      navigate(Routes.BankLinking, { state: { flow: Flow.B2B } });
    }
  };

  return (
    <Section>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Eyebrow>{i18n.t(Translations.GoalsOverviewRouteGoalsEyebrow)}</Eyebrow>
        <SerifTitle $size={30}>
          {i18n.t(Translations.GoalsOverviewRouteGoalsTitleLead)}{" "}
          <em>{i18n.t(Translations.GoalsOverviewRouteGoalsTitleHighlight)}</em>.
        </SerifTitle>
      </div>

      {standings.length > 0 && (
        <Grid>
          {standings.map((standing) => {
            const { goal, status, current, target } = standing;

            return (
              <GoalCard
                key={goal.id}
                $standing={status}
                onClick={() =>
                  navigate(Routes.GoalBreakdown, { state: {
                    goalId: goal.id,
                    beneficiaryId,
                  } })
                }
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <GoalName>{goal.name}</GoalName>
                  <Badge $standing={status}>
                    {status === Standing.Met ? (
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m5 13 4 4L19 7" />
                      </svg>
                    ) : (
                      <span className="dot" />
                    )}
                    {status === Standing.Met
                      ? i18n.t(Translations.GoalsOverviewRouteStatusMet)
                      : status === Standing.OnTrack
                        ? i18n.t(Translations.GoalsOverviewRouteStatusOnTrack)
                        : i18n.t(Translations.GoalsOverviewRouteStatusShort)}
                  </Badge>
                </div>

                <div>
                  <Amount>{formatCurrency(current, true)}</Amount>
                  <Meta>
                    <span>
                      {status === Standing.Met
                        ? i18n.t(Translations.GoalsOverviewRouteMetOverage, {
                            overage: formatCurrency(current - target, true),
                            target: formatCurrency(target),
                          })
                        : i18n.t(Translations.GoalsOverviewRouteGoalMeta, {
                            target: formatCurrency(target),
                            year: standing.targetYear,
                          })}
                    </span>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      style={{ stroke: home.ink44 }}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m9 6 6 6-6 6" />
                    </svg>
                  </Meta>
                </div>
              </GoalCard>
            );
          })}
        </Grid>
      )}

      <AddStrip onClick={handleAddGoal}>
        <span className="icon">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            style={{ stroke: home.ink72 }}
            strokeWidth="1.7"
            strokeLinecap="round"
          >
            <path d="M12 5v14" />
            <path d="M5 12h14" />
          </svg>
        </span>
        <span>
          <div className="title">
            {i18n.t(Translations.GoalsOverviewRouteAddGoalTitle, { name })}
          </div>
          <div className="body">{i18n.t(Translations.GoalsOverviewRouteAddGoalBody)}</div>
        </span>
        <span className="cta">{i18n.t(Translations.GoalsOverviewRouteAddGoalCTA)} &rarr;</span>
      </AddStrip>
    </Section>
  );
};
